// src/components/Store/useStore.js
// Estado compartido de la tienda: catálogo (GET /products) y carrito guardado
// en el navegador. Lo usan la lista de productos y la página de producto.
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../services/api';
import { loadCart, saveCart } from '../../utils/store';

// Mismo límite por producto que aplica el backend
const MAX_UNITS_PER_PRODUCT = 20;

export const maxQtyFor = (product) => Math.min(MAX_UNITS_PER_PRODUCT, product.qtyLeft ?? MAX_UNITS_PER_PRODUCT);

export const useStoreProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/products`)
      .then((res) => setProducts(res.data.products || []))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  return { products, loading, loadError };
};

export const useCart = (products) => {
  const [cart, setCart] = useState(loadCart); // { [productId]: quantity }

  useEffect(() => saveCart(cart), [cart]);

  // Productos que ya no existen / se agotaron se descartan del carrito guardado
  const cartItems = useMemo(
    () =>
      Object.entries(cart)
        .map(([id, quantity]) => {
          const product = products.find((p) => p._id === id);
          if (!product) return null;
          const maxQty = maxQtyFor(product);
          return { product, quantity: Math.min(quantity, maxQty), maxQty };
        })
        .filter((item) => item && item.quantity > 0),
    [cart, products]
  );

  // delta puede ser +1/-1 o, con { set: n }, una cantidad exacta
  const changeQty = useCallback(
    (productId, delta) => {
      const product = products.find((p) => p._id === productId);
      if (!product) return;
      setCart((prev) => {
        const target = typeof delta === 'object' ? delta.set : (prev[productId] || 0) + delta;
        const next = Math.min(maxQtyFor(product), Math.max(0, target));
        const updated = { ...prev, [productId]: next };
        if (next === 0) delete updated[productId];
        return updated;
      });
    },
    [products]
  );

  const removeFromCart = useCallback(
    (productId) =>
      setCart((prev) => {
        const updated = { ...prev };
        delete updated[productId];
        return updated;
      }),
    []
  );

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cartItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const qtyInCart = (productId) => cart[productId] || 0;

  return { cartItems, changeQty, removeFromCart, cartCount, cartTotal, qtyInCart };
};
